import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Keys, useCloseCycle, useCycleGain, useStudentsList } from '../../../api/hooks';
import type { AssessmentCycle, StudentListRow } from '../../../api/types';
import { Badge, Button, ConfirmDialog, PagedFooter, SortableTh, Table, Td, TextLink, Term } from '../../../components/ui';
import { deltaBand } from '../../../lib/bandColors';
import { apiErrorText } from '../../../lib/apiError';
import { CYCLE_STATUS_TONE } from '../../../lib/cycleStatus';
import { buildCycleCsv, downloadCsv } from '../export/csv';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { usePagination } from '../../../lib/usePagination';
import { TH_RULE } from '../../../lib/tableRules';
import { withTime } from '../../../lib/dates';

export default function CycleDetail({ cycle, canClose }: { cycle: AssessmentCycle; canClose: boolean }) {
  const qc = useQueryClient();
  const [live, setLive] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const gainQ = useCycleGain(cycle.id, live);
  const close = useCloseCycle();
  const students = useStudentsList({ grade: cycle.grade_id, ...(cycle.section_id != null ? { section: cycle.section_id } : {}) });
  const names = useMemo(() => {
    const m: Record<number, string> = {};
    (students.data?.results ?? []).forEach((s: StudentListRow) => {
      m[s.id] = `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || s.username;
    });
    return m;
  }, [students.data]);
  const gain = (live ? gainQ.data : undefined) ?? cycle.gain;
  const computedAt = (live ? gainQ.data?.computed_at : undefined) ?? gain.computed_at ?? cycle.gain_computed_at;
  const base = cycle.baseline_mastery.cells ?? {};
  const cells = gain.cells ?? {};
  const fmt = (v: number | null | undefined) => (v == null ? '—' : v.toFixed(0));

  type CycleRow = { sid: number; name: string; chapterValues: Record<number, number | null | undefined> };
  const rowObjects: CycleRow[] = cycle.target_student_ids.map((sid) => {
    const chapterValues: Record<number, number | null | undefined> = {};
    cycle.target_chapters.forEach((c) => {
      const b = base[String(sid)]?.[String(c.chapter_id)];
      const a = cells[String(sid)]?.[String(c.chapter_id)];
      chapterValues[c.chapter_id] = a ? a.after_pct : b?.pct;
    });
    return { sid, name: names[sid] ?? String(sid), chapterValues };
  });
  const cycleColumns = useMemo<SortColumn<CycleRow>[]>(() => [
    { key: 'student', accessor: (r) => r.name },
    ...cycle.target_chapters.map((c) => ({
      key: `ch-${c.chapter_id}`,
      accessor: (r: CycleRow) => r.chapterValues[c.chapter_id],
      type: 'number' as const,
    })),
    { key: 'mean_delta', accessor: (r) => gain.students?.[String(r.sid)]?.mean_delta, type: 'number' as const },
  ], [cycle.target_chapters, gain]);
  const { rows: sortedRows, sort, toggle } = useSortableTable(rowObjects, cycleColumns, { key: 'student', dir: 'asc' });
  const pg = usePagination(sortedRows, 10, `${sort?.key}${sort?.dir}`);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-ink">
        <Badge tone={CYCLE_STATUS_TONE[cycle.status]}>{cycle.status}</Badge>
        <span>{cycle.target_chapters.map((c) => c.name).join(', ')}</span>
        {cycle.target_student_ids.length < cycle.target_count && (
          <span className="text-xs text-muted">
            showing {cycle.target_student_ids.length} of {cycle.target_count} students you can access
          </span>
        )}
        {cycle.remedial_exam && <TextLink to={`/exams/report/${cycle.remedial_exam.id}`}>{cycle.remedial_exam.name} →</TextLink>}
        {cycle.status === 'diagnosed' && <TextLink to={`/exams/generate?cycle=${cycle.id}`}>Resume generation →</TextLink>}
        <span className="ml-auto flex gap-2">
          {/* The first click enables the (until now idle) live query; later
              clicks have to refetch explicitly, since the key no longer
              changes. Calling refetch() on the still-disabled observer would
              fire a live=false request, so it is guarded. */}
          <Button size="sm" variant="ghost"
                  onClick={() => {
                    if (live) gainQ.refetch(); else setLive(true);
                    // The stored gain, status and computed-at live on the list/detail rows;
                    // a nightly or manual recompute is only visible once those refetch too.
                    qc.invalidateQueries({ queryKey: ['school', 'cycles'] });
                    // exact: the gain query key extends Keys.cycle(id); it is refetched explicitly above.
                    qc.invalidateQueries({ queryKey: Keys.cycle(cycle.id), exact: true });
                  }}
                  loading={live && gainQ.isFetching}>Refresh now</Button>
          <Button size="sm" variant="ghost" onClick={() => downloadCsv(`cycle-${cycle.id}.csv`, buildCycleCsv({ ...cycle, gain }, names))}>Export CSV</Button>
          {canClose && cycle.status !== 'closed' && <Button size="sm" variant="danger-outline" onClick={() => setConfirm(true)}>Close cycle</Button>}
        </span>
        {close.isError && (
          <p className="w-full text-xs text-danger-ink">
            Could not close: {apiErrorText(close.error, 'the server rejected the request.')}
          </p>
        )}
      </div>
      <Table minWidth={560}>
        <thead><tr>
          <SortableTh columnKey="student" sort={sort} onToggle={toggle} className={TH_RULE}>Student</SortableTh>
          {cycle.target_chapters.map((c) => (
            <SortableTh key={c.chapter_id} columnKey={`ch-${c.chapter_id}`} sort={sort} onToggle={toggle}
                        align="center" className={`${TH_RULE} align-bottom`}>
              {c.name}<br /><span className="font-medium normal-case tracking-normal text-muted"><Term k="baseline">before</Term> → after (Δ)</span>
            </SortableTh>
          ))}
          <SortableTh columnKey="mean_delta" sort={sort} onToggle={toggle} align="right" help="mean_gain" className={TH_RULE}>Mean gain</SortableTh>
        </tr></thead>
        <tbody>
          {pg.pageItems.map(({ sid, name }) => {
            const st = gain.students?.[String(sid)];
            return (
              <tr key={sid}>
                <Td><Link className="font-semibold text-ink no-underline hover:text-muted-2" to={`/students/profile/${sid}`}>{name}</Link></Td>
                {cycle.target_chapters.map((c) => {
                  const b = base[String(sid)]?.[String(c.chapter_id)];
                  const a = cells[String(sid)]?.[String(c.chapter_id)];
                  const band = deltaBand(a?.delta);
                  const label = a ? `${fmt(b?.pct)} → ${fmt(a.after_pct)} (${a.delta == null ? '—' : `${a.delta > 0 ? '+' : a.delta < 0 ? '−' : ''}${Math.abs(a.delta).toFixed(0)}`})` : `${fmt(b?.pct)} → —`;
                  return <Td key={c.chapter_id} align="center"><span className={`inline-block whitespace-nowrap rounded-[8px] px-[9px] py-1 font-mono text-xs font-semibold ${band.bg} ${band.fg}`}>{label}</span></Td>;
                })}
                <Td align="right" className="whitespace-nowrap">
                  <span className="font-mono font-semibold tabular-nums">{st?.mean_delta == null ? '—' : `${st.mean_delta > 0 ? '+' : st.mean_delta < 0 ? '−' : ''}${Math.abs(st.mean_delta).toFixed(1)}`}</span>
                  {st?.crossed_pass && <> <span className="text-xs font-semibold text-success">Passed</span></>}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <PagedFooter label="Students pagination" {...pg} onPage={pg.setPage} />
      <p className="text-xs text-muted">Gain computed {computedAt ? withTime(computedAt) : 'not yet'}.</p>
      <ConfirmDialog isOpen={confirm} title="Close this cycle?" body="Its last computed gain is kept; it will no longer be recomputed."
                     confirmLabel="Close cycle" onCancel={() => setConfirm(false)}
                     onConfirm={() => { close.mutate(cycle.id); setConfirm(false); }} />
    </div>
  );
}
